'use client';

import React from 'react';
import Link from 'next/link';
import AppLogo from '@/components/ui/AppLogo';

interface SectionProps {
  num: string;
  title: string;
  children: React.ReactNode;
}

function Section({ num, title, children }: SectionProps) {
  return (
    <div className="glass rounded-2xl p-6 md:p-8 space-y-4">
      <div className="flex items-start gap-4">
        <span className="text-xs font-bold px-2.5 py-1 rounded-lg flex-shrink-0 mt-0.5" style={{ background: 'rgba(124, 111, 247, 0.15)', color: 'var(--primary)' }}>
          {num}
        </span>
        <h2 className="text-xl font-bold text-foreground">{title}</h2>
      </div>
      <div className="text-muted-foreground leading-relaxed space-y-3 text-sm md:text-base pl-0 md:pl-10">
        {children}
      </div>
    </div>
  );
}

export default function TermsPage() {
  return (
    <div className="min-h-screen" style={{ background: 'var(--background)' }}>
      {/* Ambient background */}
      <div className="fixed inset-0 pointer-events-none overflow-hidden">
        <div className="absolute top-1/4 right-1/4 w-96 h-96 rounded-full opacity-10 blur-3xl" style={{ background: 'var(--primary)' }} />
        <div className="absolute bottom-0 left-1/3 w-80 h-80 rounded-full opacity-8 blur-3xl" style={{ background: 'var(--accent)' }} />
      </div>

      {/* Topbar */}
      <header className="glass-strong border-b border-[var(--glass-border)] sticky top-0 z-50">
        <div className="max-w-screen-lg mx-auto px-6 h-16 flex items-center justify-between">
          <Link href="/landing-page" className="flex items-center gap-2">
            <AppLogo size={28} />
            <span className="font-bold text-lg gradient-text">Uyir</span>
          </Link>
          <Link href="/landing-page" className="text-sm text-muted-foreground hover:text-foreground transition-colors">
            ← Back to Home
          </Link>
        </div>
      </header>

      <main className="relative max-w-screen-lg mx-auto px-6 py-12 space-y-6">
        {/* Header */}
        <div className="text-center space-y-3 pb-4">
          <h1 className="text-3xl md:text-4xl font-bold gradient-text">Terms of Service</h1>
          <p className="text-muted-foreground text-sm">Last updated: May 2026 · Effective immediately</p>
          <p className="text-muted-foreground max-w-2xl mx-auto text-sm md:text-base">
            By using Uyir, you agree to these terms. We've kept them as human as possible.
          </p>
        </div>

        {/* Quick summary */}
        <div className="rounded-2xl p-6 space-y-3 border" style={{ background: 'rgba(124, 111, 247, 0.06)', borderColor: 'rgba(124, 111, 247, 0.25)' }}>
          <p className="font-semibold text-foreground text-sm">TL;DR</p>
          <ul className="text-sm text-muted-foreground space-y-1.5 list-disc list-inside pl-2">
            <li>Uyir is a personal emotional companion — not a medical or therapy service</li>
            <li>Your content belongs to you; we just store it securely</li>
            <li>Be kind, don't misuse the platform</li>
            <li>We can suspend accounts that violate these terms</li>
          </ul>
        </div>

        <Section num="01" title="Acceptance of Terms">
          <p>
            By accessing or using Uyir ("the Service"), you agree to be bound by these Terms of Service and our Privacy Policy. If you do not agree, please do not use the Service.
          </p>
          <p>
            These terms apply to all users, including visitors, registered users, and anyone who accesses the Service in any way.
          </p>
        </Section>

        <Section num="02" title="Description of Service">
          <p>
            Uyir is an AI-powered emotional companion application that provides a space for personal reflection, mood tracking, and conversational support. The Service includes:
          </p>
          <ul className="list-disc list-inside space-y-1 pl-2">
            <li>AI-powered chat companion</li>
            <li>Voice journaling and transcription</li>
            <li>Mood check-ins and guided sessions</li>
            <li>Expert-curated wellness content</li>
          </ul>
          <p className="mt-2 font-medium text-foreground">
            Uyir is NOT a medical service, mental health treatment, or substitute for professional therapy or crisis intervention.
          </p>
        </Section>

        <Section num="03" title="Eligibility">
          <p>You must be at least 13 years old to use Uyir. By using the Service, you represent that you meet this requirement.</p>
          <p>If you are between 13 and 18, you represent that your parent or guardian has reviewed and agreed to these terms.</p>
        </Section>

        <Section num="04" title="Your Account">
          <p>You are responsible for maintaining the confidentiality of your account credentials and for all activity that occurs under your account.</p>
          <ul className="list-disc list-inside space-y-1 pl-2">
            <li>Provide accurate registration information</li>
            <li>Keep your password secure</li>
            <li>Notify us immediately of any unauthorised access</li>
            <li>Not share your account with others</li>
          </ul>
          <p>We reserve the right to suspend or terminate accounts that violate these terms.</p>
        </Section>

        <Section num="05" title="Your Content">
          <p>
            You retain full ownership of all content you create on Uyir — journal entries, voice recordings, chat messages, and any other personal data.
          </p>
          <p>
            By using the Service, you grant Introspect Labs a limited, non-exclusive licence to store, process, and transmit your content solely for the purpose of providing the Service to you.
          </p>
          <p>
            We do not claim ownership of your content, sell it, or use it for advertising purposes.
          </p>
        </Section>

        <Section num="06" title="Acceptable Use">
          <p>You agree not to use Uyir to:</p>
          <ul className="list-disc list-inside space-y-1 pl-2">
            <li>Violate any applicable laws or regulations</li>
            <li>Harass, threaten, or harm others</li>
            <li>Attempt to reverse-engineer or scrape the Service</li>
            <li>Circumvent security measures or access controls</li>
            <li>Upload malicious code or content</li>
            <li>Impersonate other users or Introspect Labs staff</li>
          </ul>
        </Section>

        <Section num="07" title="AI Features & Limitations">
          <p>
            Uyir's AI companion is powered by OpenAI and is designed for emotional support and reflection. It is not a licensed therapist, psychologist, or medical professional.
          </p>
          <p>
            AI responses may occasionally be inaccurate, incomplete, or inappropriate. Do not rely on AI responses for medical, legal, financial, or crisis decisions.
          </p>
          <p>
            If you are experiencing a mental health crisis, please contact a qualified professional or one of the crisis helplines available in the app.
          </p>
        </Section>

        <Section num="08" title="Privacy">
          <p>
            Your use of Uyir is also governed by our <Link href="/privacy-policy" className="underline" style={{ color: 'var(--primary)' }}>Privacy Policy</Link>, which is incorporated into these Terms by reference.
          </p>
        </Section>

        <Section num="09" title="Intellectual Property">
          <p>
            The Uyir name, logo, app design, and all content created by Introspect Labs are protected by intellectual property laws. You may not reproduce, distribute, or create derivative works without our written permission.
          </p>
          <p>
            Expert content on the Discover page is owned by the respective authors and used with permission.
          </p>
        </Section>

        <Section num="10" title="Disclaimers & Limitation of Liability">
          <p>
            The Service is provided "as is" without warranties of any kind. Introspect Labs does not warrant that the Service will be uninterrupted, error-free, or free of harmful components.
          </p>
          <p>
            To the maximum extent permitted by law, Introspect Labs shall not be liable for any indirect, incidental, special, or consequential damages arising from your use of the Service.
          </p>
        </Section>

        <Section num="11" title="Termination">
          <p>
            You may delete your account at any time from Settings → Delete Account. Upon deletion, all your personal data will be permanently removed within 30 days.
          </p>
          <p>
            We may suspend or terminate your access if you violate these terms, with or without notice.
          </p>
        </Section>

        <Section num="12" title="Changes to Terms">
          <p>
            We may update these terms from time to time. We'll notify you via email and in-app notification at least 14 days before material changes take effect. Continued use after changes constitutes acceptance.
          </p>
        </Section>

        <Section num="13" title="Governing Law">
          <p>
            These terms are governed by the laws of India. Any disputes shall be subject to the exclusive jurisdiction of the courts of Mumbai, Maharashtra.
          </p>
        </Section>

        <Section num="14" title="Contact">
          <p>
            For questions about these terms, contact us at{' '}
            <a href="mailto:legal@introspectlabs.com" className="underline" style={{ color: 'var(--primary)' }}>legal@introspectlabs.com</a>{' '}
            or via our <Link href="/contact" className="underline" style={{ color: 'var(--primary)' }}>contact form</Link>.
          </p>
        </Section>

        {/* Footer nav */}
        <div className="flex flex-wrap justify-center gap-6 pt-4 text-sm text-muted-foreground">
          <Link href="/privacy-policy" className="hover:text-foreground transition-colors">Privacy Policy</Link>
          <Link href="/help-center" className="hover:text-foreground transition-colors">Help Center</Link>
          <Link href="/contact" className="hover:text-foreground transition-colors">Contact</Link>
        </div>
      </main>
    </div>
  );
}
